import { FinalExecutionOutcome, Transaction } from '@near-wallet-selector/core';
import { TokenListProvider } from '@tonic-foundation/token-list';
import JSBI from 'jsbi';
import { getAllAmms, SpinFinanceMarket, updateRoutesPools } from './amms';
import {
  computeRoutes,
  getSwapRoutes,
  getTokenRouteSegments,
  NO_PLATFORM_FEE,
} from './amms/common';
import {
  PlatformFeeAndAccounts,
  SwapMode,
  SwapRoute,
  TokenRouteSegments,
} from './amms/common/types';

import './config';

import { BN } from 'bn.js';
import { Account, connect, KeyPair, keyStores, Near } from 'near-api-js';
import { WRAPPED_NEAR_ID } from './constants';
import { loadAccountStorageBalance } from './fetchers/loadAccountStorageBalance';
import { MainnetRpc } from './jsonrpc';
import { Provider, TokenMap, UserTokensMap } from './types';
import {
  buildDepositNearTransaction,
  buildWithdrawNearTransaction,
} from './utils';
import { Percentage } from './utils/percentage';
import { registerToken } from './utils/registerToken';
import { transferToken } from './utils/transferToken';
import { checkIsMultiHopOnSameAMM } from './utils/route';

class Perk {
  near: Near;
  provider: Provider;
  tokenMap: TokenMap;
  availableTokens: string[];
  tokenRouteSegments: TokenRouteSegments;
  platformFeeAndAccounts: PlatformFeeAndAccounts;
  routeCacheDuration: number;
  intermediateTokens: string[];
  user: string | null;
  routeCache: Map<string, { fetchTimestamp: number }>;
  userTokensMapCache: Map<string, UserTokensMap>;

  constructor(
    near: Near,
    provider: Provider,
    tokenMap: TokenMap,
    availableTokens: string[],
    tokenRouteSegments: TokenRouteSegments,
    platformFeeAndAccounts: PlatformFeeAndAccounts,
    routeCacheDuration: number,
    intermediateTokens: string[],
    private wrapNear: boolean
  ) {
    this.provider = provider;
    this.near = near;

    this.tokenMap = tokenMap;
    this.availableTokens = availableTokens;
    this.tokenRouteSegments = tokenRouteSegments;

    this.platformFeeAndAccounts = platformFeeAndAccounts;
    this.routeCacheDuration = routeCacheDuration;
    this.intermediateTokens = intermediateTokens;

    this.user = null;
    this.routeCache = new Map();
    this.userTokensMapCache = new Map();
  }

  static async load({
    provider = MainnetRpc,
    user,
    privateKey,
    platformFeeAndAccounts = NO_PLATFORM_FEE, // figure out how to fetch all accounts for address
    routeCacheDuration = 0,
    wrapNear = true,
  }: {
    provider?: Provider;
    user?: string;
    privateKey?: string;
    platformFeeAndAccounts?: PlatformFeeAndAccounts;
    routeCacheDuration?: number;
    wrapNear?: boolean;
  }) {
    const keyStore = new keyStores.InMemoryKeyStore();

    if (user && privateKey) {
      const keyPair = KeyPair.fromString(privateKey);
      await keyStore.setKey('mainnet', user, keyPair);
    }

    const near = await connect({
      networkId: 'mainnet',
      nodeUrl: 'https://rpc.mainnet.near.org',
      keyStore,
      headers: {},
    });

    const [tokenRouteSegments, availableTokens] =
      await Perk.fetchTokenRouteSegments(provider);

    const tokenMap = await new TokenListProvider().resolve().then((tokens) => {
      const tokenList = tokens.filterByNearEnv('mainnet').getList();

      const tokenMap = tokenList.reduce((map, item) => {
        map.set(item.address, item);
        return map;
      }, new Map());

      return tokenMap;
    });

    const intermediateTokens = Perk.getIntermediateTokens();

    const perk = new Perk(
      near,
      provider,
      tokenMap,
      availableTokens,
      tokenRouteSegments,
      platformFeeAndAccounts,
      routeCacheDuration,
      intermediateTokens,
      wrapNear
    );

    if (user) perk.setUserPublicKey(user);

    return perk;
  }

  static async fetchTokenRouteSegments(
    provider: Provider
  ): Promise<[TokenRouteSegments, string[]]> {
    const amms = await getAllAmms(provider);
    const tokenRouteSegments = getTokenRouteSegments(amms);
    const availableTokens = Array.from(
      new Set(amms.flatMap((amm) => amm.reserveTokenMints))
    );

    return [tokenRouteSegments, availableTokens];
  }

  static getIntermediateTokens() {
    return [
      'wrap.near',
      'usn',
      'dac17f958d2ee523a2206206994597c13d831ec7.factory.bridge.near',
      'a0b86991c6218b36c1d19d4a2e9eb0ce3606eb48.factory.bridge.near',
      'token.pembrock.near',
    ];
  }

  setUserPublicKey(user: string) {
    this.user = user;
  }

  private async loadAndSavePlatformFeeTokenData(tokenId: string) {
    try {
      const { storageBalance } = await loadAccountStorageBalance({
        provider: this.provider,
        tokenId,
        tokenOwnerId: this.platformFeeAndAccounts.feeAccountId,
      });

      this.platformFeeAndAccounts.feeAccountTokens.set(tokenId, storageBalance);

      return storageBalance;
    } catch (e) {
      console.warn('Unable to load token data for platform fee account', e);
      return null;
    }
  }

  private async getPlatformFeeBpsForToken(outputMint: string) {
    const platformFeeAccountHasToken =
      this.platformFeeAndAccounts.feeAccountTokens.has(outputMint);

    const platformFeeAccountTokenInfo = platformFeeAccountHasToken
      ? this.platformFeeAndAccounts.feeAccountTokens.get(outputMint)
      : await this.loadAndSavePlatformFeeTokenData(outputMint);

    const platformFeeBps = platformFeeAccountTokenInfo
      ? this.platformFeeAndAccounts.feeBps
      : 0;

    return platformFeeBps;
  }

  async computeRoutes({
    inputMint,
    outputMint,
    amount,
    slippage = 0,
    forceFetch = false,
    swapMode = SwapMode.ExactIn,
    simpleRoutesOnly = false,
  }: {
    inputMint: string;
    outputMint: string;
    amount: JSBI;
    slippage?: number;
    forceFetch?: boolean;
    swapMode?: SwapMode;
    simpleRoutesOnly?: boolean;
  }) {
    const feeBps = await this.getPlatformFeeBpsForToken(outputMint);
    const feeBpsPercentage = Percentage.fromFeeBpsNumber(feeBps);
    const now = new Date().getTime(); // do sort so that it's always the same order for the same inputMint and outputMint and vice versa

    const swapType = simpleRoutesOnly ? 'simple' : 'complex';
    const inputMintAndOutputMint = [inputMint, outputMint, swapType]
      .sort((a, b) => a.localeCompare(b))
      .join('');

    const routeCache = this.routeCache.get(inputMintAndOutputMint);
    const allRoutes = computeRoutes({
      inputMint,
      outputMint,
      tokenRouteSegments: this.tokenRouteSegments,
      intermediateTokens: this.intermediateTokens,
    });

    const routes = allRoutes.filter((route) => {
      // we're sure that user's funds won't stuck in intermediate tokens
      // for direct routes or swap route with same AMMs in steps
      if (simpleRoutesOnly) {
        const isDirectRoute = route.length === 1;
        const isMultiHopOnSameAMM = checkIsMultiHopOnSameAMM(
          route.map((step) => step.amm)
        );

        // for spin it's still not safe, if there will be more not implemented safe logic
        // for multi hop it would be better to keep list somewhere or determine it in either way
        if (route.some((step) => step.amm instanceof SpinFinanceMarket)) {
          return false;
        }

        return isDirectRoute || isMultiHopOnSameAMM;
      }

      return true;
    });

    let shouldBustCache = false; // special -1 condition to not fetch

    if (this.routeCacheDuration === -1) {
      shouldBustCache = false;
    } else if (this.routeCacheDuration === 0) {
      shouldBustCache = true;
    } else {
      if (routeCache) {
        const { fetchTimestamp } = routeCache;

        if (now - fetchTimestamp > this.routeCacheDuration) {
          shouldBustCache = true;
        }
      } else {
        shouldBustCache = true;
      }
    }

    if (forceFetch || shouldBustCache) {
      // console.time('update pools');
      await updateRoutesPools(this.provider, routes);
      // console.timeEnd('update pools');

      this.routeCache.set(inputMintAndOutputMint, {
        fetchTimestamp: new Date().getTime(),
      });
    }

    try {
      // TODO: maybe add to cache and update on every biuld transactions
      const tokensMapInCache = this.userTokensMapCache.get(
        inputMintAndOutputMint
      );

      const userTokensMap: UserTokensMap = tokensMapInCache
        ? tokensMapInCache
        : new Map();

      if (this.user !== null && !tokensMapInCache) {
        const tokensToCheck: string[] = [];
        // Register for intermediate and output mints
        routes.forEach((route) =>
          route.forEach(({ amm }) =>
            amm.reserveTokenMints.forEach((token) => tokensToCheck.push(token))
          )
        );

        // console.time('loading user tokens for routes');

        const loadedUserTokens = await Promise.all(
          tokensToCheck.map((token) =>
            loadAccountStorageBalance({
              provider: this.provider,
              tokenId: token,
              // @ts-ignore
              tokenOwnerId: this.user,
            })
          )
        );

        // console.timeEnd('loading user tokens for routes');

        loadedUserTokens.forEach(({ tokenId, existing, storageBalance }) =>
          userTokensMap.set(tokenId, { existing, data: storageBalance })
        );

        this.userTokensMapCache.set(inputMintAndOutputMint, userTokensMap);
      }

      const slippagePercentage = Percentage.fromSlippageNumber(slippage);
      const routesInfos = getSwapRoutes({
        routes,
        inputMint,
        outputMint,
        amount,
        userTokensMap,
        slippage: slippagePercentage,
        feeBps: feeBpsPercentage,
        swapMode,
      });
      return {
        routesInfos,
        /* indicate if the result is fetched or get from cache */
        cached: !(forceFetch || shouldBustCache),
      };
    } catch (e) {
      throw e;
    } finally {
      // clear cache if it is expired
      this.routeCache.forEach(({ fetchTimestamp }, key) => {
        if (fetchTimestamp - now > this.routeCacheDuration) {
          this.routeCache.delete(key);
        }
      });
    }
  }

  async buildTransactions({ route }: { route: SwapRoute }): Promise<{
    transactions: Transaction[];
    transactionsBySteps: Transaction[][];
  }> {
    const transactions = [];

    const isMultiHopOnSameAMM = checkIsMultiHopOnSameAMM(
      route.steps.map((step) => step.amm)
    );

    // for multi-hop there only one tx for double-step route
    const transactionsBySteps: Transaction[][] = isMultiHopOnSameAMM
      ? [[]]
      : route.steps.map(() => []);

    const feeBps = await this.getPlatformFeeBpsForToken(route.outputMint);
    const feeBpsPercentage = Percentage.fromFeeBpsNumber(feeBps);

    if (!this.user) {
      throw new Error('No user provided');
    }
    const user = this.user;

    const account = new Account(this.near.connection, this.user);

    const tokensToRegister = new Set<string>();

    // wrap native near if not enough wrapped
    if (this.wrapNear && route.inputMint === WRAPPED_NEAR_ID) {
      console.log('Wrap near: ', route.amountIn.toString());
      const depositTx = await buildDepositNearTransaction(
        account,
        route.amountIn
      );

      if (depositTx.actions.length > 0) {
        transactions.push(depositTx);
        transactionsBySteps[0].push(depositTx);
        tokensToRegister.add(WRAPPED_NEAR_ID);
      }
    }

    // Register for intermediate and output mints
    route.steps.forEach((_) => tokensToRegister.add(_.outputMint));
    const tokensToRegisterList = [...tokensToRegister.values()];

    const registerTokensTxs = (
      await Promise.all(
        tokensToRegisterList.map((token) =>
          registerToken(this.provider, token, user)
        )
      )
    ).filter((_): _ is Transaction => !!_);

    transactions.push(...registerTokensTxs);
    transactionsBySteps[0].push(...registerTokensTxs);

    // same amm for each step
    if (isMultiHopOnSameAMM) {
      const commonAMM = route.steps[0].amm;

      const swapTransactions = await commonAMM.createSwapRouteInstructions({
        provider: this.provider,
        user: this.user,
        swapRoute: route,
      });

      transactions.push(...swapTransactions);
      transactionsBySteps[transactionsBySteps.length - 1].push(
        ...swapTransactions
      );
    } else {
      let index = 0;

      for (let routeStep of route.steps) {
        const stepTransactions = await routeStep.amm.createSwapInstructions({
          provider: this.provider,
          user: this.user,
          swapStep: routeStep,
        });

        transactions.push(...stepTransactions);
        transactionsBySteps[index].push(...stepTransactions);

        index++;
      }
    }

    let totalAmountOut = route.minAmountOut;

    // if feeBps zero - no fee set up or no token on fee account
    if (feeBps !== 0 && this.platformFeeAndAccounts.feeAccountId) {
      const tokenToUse =
        route.swapMode === SwapMode.ExactIn
          ? route.outputMint
          : route.inputMint;

      const amountToUse =
        route.swapMode === SwapMode.ExactIn ? route.amountOut : route.amountIn;

      const feeAmount = feeBpsPercentage.getFrom(JSBI.BigInt(amountToUse));

      transactions.push(
        transferToken({
          tokenId: tokenToUse,
          accountFrom: this.user,
          accountTo: this.platformFeeAndAccounts.feeAccountId,
          amount: feeAmount,
        })
      );

      if (route.swapMode === SwapMode.ExactIn) {
        totalAmountOut = JSBI.subtract(totalAmountOut, feeAmount);
      }
    }

    // send to native near what we receive from swap
    if (this.wrapNear && route.outputMint === WRAPPED_NEAR_ID) {
      const unwrapTx = await buildWithdrawNearTransaction(user, totalAmountOut);

      transactions.push(unwrapTx);
      transactionsBySteps[transactionsBySteps.length - 1].push(unwrapTx);
    }

    // reset cache because new tokens can be created using this txs
    if (tokensToRegisterList.length > 0) {
      this.userTokensMapCache.clear();
    }

    return {
      transactions,
      transactionsBySteps,
    };
  }

  async exchange({
    route,
  }: {
    route: SwapRoute;
  }): Promise<FinalExecutionOutcome[]> {
    const { transactions } = await this.buildTransactions({ route });

    if (!this.user) {
      throw new Error('No user provided');
    }

    const userAccount = await this.near.account(this.user);
    const txResults = [];

    for (const transaction of transactions) {
      for (const action of transaction.actions) {
        if (action.type !== 'FunctionCall') {
          throw new Error('Non function call in exchange');
        }

        const result = await userAccount.functionCall({
          contractId: transaction.receiverId,
          methodName: action.params.methodName,
          args: action.params.args,
          gas: new BN(action.params.gas),
          attachedDeposit: new BN(action.params.deposit),
        });

        txResults.push(result);
      }
    }

    return txResults;
  }
}

export { Perk };
